import { useState } from 'react';
import {
  AUTH_METHODS,
  type AuthMethod,
  type SupplierApiConfigInput,
  type SupplierFieldMapping,
  type SupplierPreset,
} from '@aggregator/shared';
import type { CreateSupplierInput } from '@aggregator/shared';
import { Input } from '@/components/ui/input';
import { Field } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { FieldMappingEditor } from '@/components/FieldMappingEditor';
import type { AdminSupplier, SupplierProbeResult } from '@/api/types';

type ApiConfigForm = Partial<SupplierApiConfigInput> & {
  baseUrl: string;
  apiKey: string;
  authMethod: AuthMethod;
};

export interface SupplierFormValue {
  name: string;
  isActive: boolean;
  walletAlertThreshold: number;
  syncIntervalMinutes: number;
  apiConfig: ApiConfigForm;
  fieldMappings: SupplierFieldMapping[];
}

interface PresetMeta {
  value: SupplierPreset;
  label: string;
  description: string;
  /** Config to merge in when the preset is picked (only fills blanks). */
  defaults?: Partial<ApiConfigForm>;
}

export const PRESETS: PresetMeta[] = [
  {
    value: 'xproseller',
    label: 'XPro Seller Partner API',
    description: 'XPro Seller — bearer auth, paginated catalog, wallet balance, idempotent orders and delivery.lines.',
    defaults: {
      authMethod: 'bearer', productsPath: 'catalog/products', createOrderPath: 'orders', orderStatusPath: 'orders/{orderId}',
      balancePath: 'balance', accountPath: 'balance', balancePathField: 'balance', orderProductParam: 'productId',
      orderQuantityParam: 'quantity', orderProductIdNumeric: true, orderIdPathField: 'order.orderId',
      orderStatusPathField: 'order.status', deliveryDataPathField: 'delivery',
    },
  },
  {
    value: 'generic',
    label: 'Generic (config-driven)',
    description: 'Works for most REST APIs. You set the paths + field mappings below.',
  },
  {
    value: 'aivaulthub',
    label: 'AI Vault Hub',
    description: 'reseller.aivaulthub.store — cents pricing, stock quantities, /me balance and delivered product codes.',
    defaults: {
      authMethod: 'api_key_header',
      authHeaderName: 'X-API-Key',
      productsPath: '/api/v1/products',
      createOrderPath: '/api/v1/order',
      orderStatusPath: '/api/v1/order/{orderId}',
      balancePath: '/api/v1/me',
      accountPath: '/api/v1/me',
      balancePathField: 'balance',
      orderProductParam: 'service_id',
      orderQuantityParam: 'quantity',
      orderProductIdNumeric: false,
      orderIdPathField: 'order_id',
      orderStatusPathField: 'status',
      deliveryDataPathField: 'products',
    },
  },
  {
    value: 'insightxstore',
    label: 'InsightX Store',
    description:
      'insightxstore-bot-production.up.railway.app — /api/v1/products · /balance · POST /orders. Endpoints are built in.',
    defaults: {
      authMethod: 'bearer',
      productsPath: '/api/v1/products',
      createOrderPath: '/api/v1/orders',
      orderStatusPath: '/api/v1/orders/{orderId}',
      balancePath: '/api/v1/balance',
      balancePathField: 'balance',
    },
  },
  {
    value: 'warzoneshop',
    label: 'Warzone Shop',
    description:
      'api.warzoneshop.in — /api/v1/products · /me · POST /order. Auth X-API-Key. Endpoints are built in.',
    defaults: {
      authMethod: 'api_key_header',
      authHeaderName: 'X-API-Key',
      productsPath: '/api/v1/products',
      createOrderPath: '/api/v1/order',
      orderStatusPath: '/api/v1/order/{orderId}',
      balancePath: '/api/v1/me',
      accountPath: '/api/v1/me',
      balancePathField: 'wallet_balance',
      orderProductParam: 'service_id',
      orderProductIdNumeric: false,
    },
  },
];

const ADVANCED_KEYS: Array<[keyof ApiConfigForm, string, string]> = [
  ['productsArrayPath', 'Product array field (dot-path)', 'data / data.products / result.items'],
  ['createOrderPath', 'Create-order path', '/api/v1/orders'],
  ['orderStatusPath', 'Order-status path', '/api/v1/orders/{orderId}'],
  ['cancelOrderPath', 'Cancel-order path', '/api/v1/orders/{orderId}/cancel'],
  ['balancePath', 'Balance path', '/api/v1/balance'],
  ['accountPath', 'Account/profile path', '/api/v1/me'],
  ['balancePathField', 'Balance field (dot-path)', 'balance / wallet_balance'],
  ['ledgerPathField', 'Ledger field (dot-path)', 'ledger'],
  ['orderProductParam', 'Order product-id body key', 'product_id / service_id'],
  ['orderQuantityParam', 'Order quantity body key', 'quantity'],
  ['orderIdPathField', 'Order-id field (dot-path)', 'order_id'],
  ['orderStatusPathField', 'Order-status field (dot-path)', 'status / order.status'],
  ['deliveryDataPathField', 'Delivery/codes field (dot-path)', 'codes / order.delivered_products'],
];

export function emptySupplierForm(): SupplierFormValue {
  return {
    name: '',
    isActive: true,
    walletAlertThreshold: 0,
    syncIntervalMinutes: 60,
    apiConfig: { baseUrl: '', apiKey: '', authMethod: 'bearer', preset: 'generic' },
    fieldMappings: [],
  };
}

export function supplierToForm(s: AdminSupplier): SupplierFormValue {
  return {
    name: s.name,
    isActive: s.isActive,
    walletAlertThreshold: s.walletAlertThreshold,
    syncIntervalMinutes: s.syncIntervalMinutes,
    apiConfig: { ...s.apiConfig, apiKey: '', preset: s.apiConfig.preset ?? 'generic' },
    fieldMappings: s.fieldMappings ?? [],
  };
}

const STRING_KEYS: Array<keyof ApiConfigForm> = [
  'authHeaderName',
  'authQueryParam',
  'productsPath',
  'productsArrayPath',
  'createOrderPath',
  'orderStatusPath',
  'cancelOrderPath',
  'balancePath',
  'accountPath',
  'balancePathField',
  'ledgerPathField',
  'orderProductParam',
  'orderQuantityParam',
  'orderIdPathField',
  'orderStatusPathField',
  'deliveryDataPathField',
];

function buildConfig(v: SupplierFormValue, mode: 'create' | 'update'): Record<string, unknown> {
  const cfg: Record<string, unknown> = {
    baseUrl: v.apiConfig.baseUrl,
    authMethod: v.apiConfig.authMethod,
  };
  if (v.apiConfig.preset && v.apiConfig.preset !== 'generic') cfg['preset'] = v.apiConfig.preset;
  if (v.apiConfig.apiKey) cfg['apiKey'] = v.apiConfig.apiKey;
  else if (mode === 'create') cfg['apiKey'] = '';
  if (typeof v.apiConfig.orderProductIdNumeric === 'boolean') {
    cfg['orderProductIdNumeric'] = v.apiConfig.orderProductIdNumeric;
  }
  for (const k of STRING_KEYS) {
    const val = v.apiConfig[k];
    if (typeof val === 'string' && val.trim()) cfg[k] = val.trim();
  }
  return cfg;
}

/** Build the create/update payload — omit apiKey when left blank (edit mode). */
export function formToPayload(v: SupplierFormValue, mode: 'create' | 'update'): CreateSupplierInput {
  return {
    name: v.name,
    isActive: v.isActive,
    walletAlertThreshold: v.walletAlertThreshold,
    syncIntervalMinutes: v.syncIntervalMinutes,
    apiConfig: buildConfig(v, mode) as CreateSupplierInput['apiConfig'],
    fieldMappings: v.fieldMappings,
  };
}

/** apiConfig for a probe call (needs a non-empty apiKey to satisfy the schema). */
export function formToApiConfig(v: SupplierFormValue): Record<string, unknown> {
  const cfg = buildConfig(v, 'create');
  if (!cfg['apiKey']) cfg['apiKey'] = 'preview-only';
  return cfg;
}

export function SupplierForm({
  value,
  onChange,
  mode,
  onFetchSample,
}: {
  value: SupplierFormValue;
  onChange: (v: SupplierFormValue) => void;
  mode: 'create' | 'update';
  onFetchSample?: () => Promise<SupplierProbeResult>;
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showMapping, setShowMapping] = useState(mode === 'update');
  const set = (patch: Partial<SupplierFormValue>) => onChange({ ...value, ...patch });
  const setCfg = (patch: Partial<ApiConfigForm>) =>
    onChange({ ...value, apiConfig: { ...value.apiConfig, ...patch } });

  const preset = value.apiConfig.preset ?? 'generic';
  const presetMeta = PRESETS.find((p) => p.value === preset) ?? PRESETS[0]!;

  function choosePreset(next: SupplierPreset) {
    const meta = PRESETS.find((p) => p.value === next);
    const merged: ApiConfigForm = { ...value.apiConfig, preset: next };
    if (meta?.defaults) {
      for (const [k, dv] of Object.entries(meta.defaults)) {
        const cur = merged[k as keyof ApiConfigForm];
        if (cur === undefined || cur === '' || k === 'authMethod' || k === 'authHeaderName') {
          (merged as Record<string, unknown>)[k] = dv;
        }
      }
    }
    onChange({ ...value, apiConfig: merged });
  }

  return (
    <div className="space-y-4">
      <Field label="Name">
        <Input value={value.name} onChange={(e) => set({ name: e.target.value })} placeholder="Warzone Shop" />
      </Field>

      <Field label="Adapter preset" hint={presetMeta.description}>
        <Select
          value={preset}
          onChange={(e) => choosePreset(e.target.value as SupplierPreset)}
          options={PRESETS.map((p) => ({ value: p.value, label: p.label }))}
        />
      </Field>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label="Sync interval (minutes)">
          <Input
            type="number"
            min={1}
            value={value.syncIntervalMinutes}
            onChange={(e) => set({ syncIntervalMinutes: Number(e.target.value) || 60 })}
          />
        </Field>
        <Field label="Wallet alert threshold (USDT)">
          <Input
            type="number"
            min={0}
            step="0.01"
            value={value.walletAlertThreshold}
            onChange={(e) => set({ walletAlertThreshold: Number(e.target.value) || 0 })}
          />
        </Field>
      </div>

      <Field label="API base URL">
        <Input
          value={value.apiConfig.baseUrl}
          onChange={(e) => setCfg({ baseUrl: e.target.value })}
          placeholder="https://api.warzoneshop.in"
        />
      </Field>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <Field label={mode === 'update' ? 'API key (leave blank to keep)' : 'API key'}>
          <Input
            type="password"
            value={value.apiConfig.apiKey}
            onChange={(e) => setCfg({ apiKey: e.target.value })}
            placeholder={mode === 'update' ? '••••••••' : 'WAR_… / sk_live_…'}
          />
        </Field>
        <Field label="Auth method" hint="bearer → Authorization: Bearer · api_key_header → custom header">
          <Select
            value={value.apiConfig.authMethod}
            onChange={(e) => setCfg({ authMethod: e.target.value as AuthMethod })}
            options={AUTH_METHODS.map((m) => ({ value: m, label: m }))}
          />
        </Field>
      </div>

      {value.apiConfig.authMethod === 'api_key_header' ? (
        <Field label="Auth header name" hint='e.g. "X-API-Key"'>
          <Input value={value.apiConfig.authHeaderName ?? ''} onChange={(e) => setCfg({ authHeaderName: e.target.value })} />
        </Field>
      ) : null}
      {value.apiConfig.authMethod === 'query_param' ? (
        <Field label="Auth query param" hint='e.g. "token"'>
          <Input value={value.apiConfig.authQueryParam ?? ''} onChange={(e) => setCfg({ authQueryParam: e.target.value })} />
        </Field>
      ) : null}

      <Field label="Products path" hint="Relative to base URL. Default: /products">
        <Input
          value={value.apiConfig.productsPath ?? ''}
          onChange={(e) => setCfg({ productsPath: e.target.value })}
          placeholder="/api/v1/products"
        />
      </Field>

      <label className="flex items-center gap-2 text-sm">
        <input type="checkbox" checked={value.isActive} onChange={(e) => set({ isActive: e.target.checked })} />
        Active
      </label>

      <button
        type="button"
        className="text-xs text-primary hover:underline"
        onClick={() => setShowAdvanced((s) => !s)}
      >
        {showAdvanced ? 'Hide' : 'Show'} advanced endpoint paths
        {preset !== 'generic' ? ' (optional — preset has defaults)' : ''}
      </button>

      {showAdvanced ? (
        <div className="grid grid-cols-1 gap-3 rounded-md border p-3 sm:grid-cols-2">
          {ADVANCED_KEYS.map(([key, label, ph]) => (
            <Field key={String(key)} label={label}>
              <Input
                value={(value.apiConfig[key] as string | undefined) ?? ''}
                onChange={(e) => setCfg({ [key]: e.target.value } as Partial<ApiConfigForm>)}
                placeholder={ph}
              />
            </Field>
          ))}
        </div>
      ) : null}

      {onFetchSample ? (
        <div className="rounded-md border">
          <button
            type="button"
            className="flex w-full items-center justify-between px-3 py-2.5 text-sm font-medium"
            onClick={() => setShowMapping((s) => !s)}
          >
            <span>
              🔌 Test API &amp; map fields
              {value.fieldMappings.length > 0 ? (
                <span className="ml-2 text-xs text-muted-foreground">{value.fieldMappings.length} mapped</span>
              ) : null}
            </span>
            <span className="text-xs text-primary">{showMapping ? 'Hide' : 'Open'}</span>
          </button>
          {showMapping ? (
            <div className="border-t p-3">
              <FieldMappingEditor
                mappings={value.fieldMappings}
                onChange={(fieldMappings) => set({ fieldMappings })}
                onFetch={onFetchSample}
                hint="Fill in the base URL, key and products path above, then fetch a sample and auto-match."
              />
            </div>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
